import { useState, useEffect } from "react";
import { Check, Crown, Loader2, AlertCircle, XCircle, ArrowLeftRight, RotateCcw, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSearchParams, useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

const BASIC_FEATURES = ["dashboard", "calendar", "weekly", "inventory", "workers", "shift-types", "training-plans"];
const PREMIUM_FEATURES = [...BASIC_FEATURES, "nutrition", "anamnese", "bioimpedance", "social-media", "ai-chat", "statistics", "integrations", "crm"];

const FEATURE_KEYS = {
  dashboard: "nav_dashboard", calendar: "nav_calendar", weekly: "nav_weekly",
  inventory: "nav_inventory", workers: "nav_workers", "shift-types": "nav_shift_types",
  "training-plans": "nav_training_plans", nutrition: "nav_nutrition", anamnese: "nav_anamnese",
  bioimpedance: "nav_bioimpedance", "social-media": "nav_social_media",
  "ai-chat": "nav_ai_chat", statistics: "nav_statistics", integrations: "nav_integrations",
  crm: "nav_crm",
};

export default function SubscriptionPage({ onLogout, subscription, onSubscriptionChange }) {
  const { t } = useLanguage();
  const [billing, setBilling] = useState("monthly");
  const [loading, setLoading] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showChangeDialog, setShowChangeDialog] = useState(false);
  const [changeTo, setChangeTo] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const planFromUrl = searchParams.get("plan");
  const billingFromUrl = searchParams.get("billing");
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!sessionId) return;
    setCheckingPayment(true);
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/api/subscription/checkout/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          if (token && planFromUrl) {
            await axios.post(`${API}/api/subscription/activate?plan=${planFromUrl}&billing=${billingFromUrl || 'monthly'}&session_id=${sessionId}`, {}, { headers });
          }
          toast.success("Abonnement aktiviert!");
          onSubscriptionChange();
          setCheckingPayment(false);
          navigate("/", { replace: true });
          return;
        }
      } catch (err) { console.error("Payment check failed:", err); }
      attempts++;
      if (attempts < 10) setTimeout(poll, 2000);
      else { setCheckingPayment(false); toast.error("Zahlungsprüfung fehlgeschlagen"); }
    };
    poll();
  }, [sessionId]);

  const handleSubscribe = async (plan) => {
    setLoading(plan);
    try {
      const res = await axios.post(`${API}/api/subscription/checkout`, { plan, billing, origin_url: window.location.origin }, { headers });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
      setLoading(null);
    }
  };

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/api/subscription/cancel`, {}, { headers });
      toast.success("Abonnement wird zum Ende der Laufzeit gekündigt");
      setShowCancelDialog(false);
      onSubscriptionChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    setActionLoading(false);
  };

  const handleReactivate = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/api/subscription/reactivate`, {}, { headers });
      toast.success("Kündigung zurückgenommen");
      onSubscriptionChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    setActionLoading(false);
  };

  const handleChangePlan = async () => {
    if (!changeTo) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/api/subscription/change-plan?plan=${changeTo}`, {}, { headers });
      toast.success(`Plan gewechselt zu ${changeTo === "premium" ? "Premium" : "Basic"}`);
      setShowChangeDialog(false);
      onSubscriptionChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    setActionLoading(false);
  };

  const isActive = subscription?.status === "active";
  const isPastDue = subscription?.status === "past_due";
  const isCanceling = subscription?.cancel_at_period_end;
  const currentPlan = subscription?.plan;
  const periodEnd = subscription?.current_period_end;

  if (checkingPayment) {
    return (
      <Layout onLogout={onLogout}>
        <div className="flex flex-col items-center justify-center h-full gap-4">
          <Loader2 className="w-10 h-10 animate-spin text-slate-400" />
          <p className="text-slate-600">Zahlung wird überprüft...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout onLogout={onLogout}>
      <div className="p-6 h-full overflow-y-auto" data-testid="subscription-page">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Crown className="w-6 h-6 text-amber-500" />
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {isActive ? "Dein Abonnement" : "Plan wählen"}
              </h1>
            </div>

            {/* Active subscription card */}
            {isActive && !isCanceling && (
              <div className="mt-4 p-5 bg-emerald-50 border border-emerald-200 rounded-xl" data-testid="active-sub-card">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Check className="w-6 h-6 text-emerald-500" />
                    <div>
                      <p className="text-base font-bold text-emerald-800">{currentPlan === "premium" ? "Premium" : "Basic"} Plan — Aktiv</p>
                      {periodEnd && (
                        <p className="text-sm text-emerald-600 flex items-center gap-1 mt-0.5">
                          <CalendarClock className="w-3.5 h-3.5" /> Nächste Zahlung: {new Date(periodEnd).toLocaleDateString("de-DE")}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {currentPlan === "basic" && (
                      <Button size="sm" onClick={() => { setChangeTo("premium"); setShowChangeDialog(true); }} className="bg-slate-900 hover:bg-slate-800" data-testid="upgrade-btn">
                        <ArrowLeftRight className="w-3.5 h-3.5 mr-1.5" /> Upgrade
                      </Button>
                    )}
                    {currentPlan === "premium" && (
                      <Button size="sm" variant="outline" onClick={() => { setChangeTo("basic"); setShowChangeDialog(true); }} data-testid="downgrade-btn">
                        <ArrowLeftRight className="w-3.5 h-3.5 mr-1.5" /> Downgrade
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setShowCancelDialog(true)} data-testid="cancel-sub-btn">
                      <XCircle className="w-3.5 h-3.5 mr-1.5" /> Kündigen
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Canceling notice */}
            {isCanceling && (
              <div className="mt-4 p-5 bg-amber-50 border border-amber-200 rounded-xl" data-testid="canceling-sub-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-base font-bold text-amber-800">{currentPlan === "premium" ? "Premium" : "Basic"} — Wird gekündigt</p>
                    {periodEnd && <p className="text-sm text-amber-600">Zugang bis: {new Date(periodEnd).toLocaleDateString("de-DE")}</p>}
                  </div>
                  <Button size="sm" onClick={handleReactivate} disabled={actionLoading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="reactivate-btn">
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Kündigung zurücknehmen</>}
                  </Button>
                </div>
              </div>
            )}

            {/* Past due warning */}
            {isPastDue && (
              <div className="mt-4 p-5 bg-red-50 border border-red-200 rounded-xl" data-testid="past-due-card">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-6 h-6 text-red-500" />
                  <div>
                    <p className="text-base font-bold text-red-800">Zahlung ausstehend</p>
                    <p className="text-sm text-red-600">Bitte aktualisieren Sie Ihre Zahlungsmethode, um den Zugang nicht zu verlieren.</p>
                  </div>
                </div>
              </div>
            )}

            {/* No subscription */}
            {!isActive && !isPastDue && !isCanceling && (
              <div className="flex items-center gap-2 mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="no-subscription-banner">
                <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                <p className="text-sm text-amber-800">Wählen Sie einen Plan, um Athlo Nexus zu nutzen.</p>
              </div>
            )}
          </div>

          {/* Billing Toggle */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center bg-slate-100 rounded-full p-1">
              <button onClick={() => setBilling("monthly")}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${billing === "monthly" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
                data-testid="sub-billing-monthly">Monatlich</button>
              <button onClick={() => setBilling("annual")}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${billing === "annual" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
                data-testid="sub-billing-annual">
                Jährlich <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">2 Monate gratis</span>
              </button>
            </div>
          </div>

          {/* Plans */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Basic */}
            <div className={`border rounded-xl p-6 bg-white ${currentPlan === "basic" ? "border-emerald-300 ring-2 ring-emerald-100" : "border-slate-200"}`} data-testid="sub-plan-basic">
              <h3 className="text-lg font-bold text-slate-900">Basic</h3>
              <div className="flex items-baseline gap-1 mt-2 mb-4">
                <span className="text-3xl font-bold">{billing === "annual" ? "11.67" : "14"}</span>
                <span className="text-slate-500 text-sm">€/Monat</span>
              </div>
              {billing === "annual" && <p className="text-xs text-slate-400 -mt-3 mb-4">€140/Jahr</p>}
              {currentPlan === "basic" && isActive ? (
                <Button variant="outline" disabled className="w-full mb-4">Aktueller Plan</Button>
              ) : (
                <Button variant="outline" className="w-full mb-4" onClick={() => handleSubscribe("basic")} disabled={loading === "basic"} data-testid="subscribe-basic">
                  {loading === "basic" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Basic wählen"}
                </Button>
              )}
              <ul className="space-y-2.5">
                {BASIC_FEATURES.map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" /> {t(FEATURE_KEYS[f])}
                  </li>
                ))}
              </ul>
            </div>

            {/* Premium */}
            <div className={`border-2 rounded-xl p-6 bg-white relative ${currentPlan === "premium" ? "border-emerald-400 ring-2 ring-emerald-100" : "border-slate-900"}`} data-testid="sub-plan-premium">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-xs font-semibold px-3 py-1 rounded-full">Empfohlen</div>
              <h3 className="text-lg font-bold text-slate-900">Premium</h3>
              <div className="flex items-baseline gap-1 mt-2 mb-4">
                <span className="text-3xl font-bold">{billing === "annual" ? "15.83" : "19"}</span>
                <span className="text-slate-500 text-sm">€/Monat</span>
              </div>
              {billing === "annual" && <p className="text-xs text-slate-400 -mt-3 mb-4">€190/Jahr</p>}
              {currentPlan === "premium" && isActive ? (
                <Button disabled className="w-full mb-4 bg-emerald-600">Aktueller Plan</Button>
              ) : (
                <Button className="w-full mb-4 bg-slate-900 hover:bg-slate-800" onClick={() => handleSubscribe("premium")} disabled={loading === "premium"} data-testid="subscribe-premium">
                  {loading === "premium" ? <Loader2 className="w-4 h-4 animate-spin" /> : currentPlan === "basic" ? "Upgrade zu Premium" : "Premium wählen"}
                </Button>
              )}
              <ul className="space-y-2.5">
                {PREMIUM_FEATURES.map(f => {
                  const isPremiumOnly = !BASIC_FEATURES.includes(f);
                  return (
                    <li key={f} className="flex items-center gap-2 text-sm text-slate-700">
                      <Check className={`w-4 h-4 flex-shrink-0 ${isPremiumOnly ? "text-violet-500" : "text-emerald-500"}`} />
                      <span className={isPremiumOnly ? "font-medium" : ""}>{t(FEATURE_KEYS[f])}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-700">Abonnement kündigen?</DialogTitle>
            <DialogDescription>
              Ihr Zugang bleibt bis zum Ende der aktuellen Laufzeit bestehen{periodEnd ? ` (${new Date(periodEnd).toLocaleDateString("de-DE")})` : ""}.
              Danach verlieren Sie den Zugang zu allen Funktionen.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" className="flex-1" onClick={() => setShowCancelDialog(false)}>Abbrechen</Button>
            <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={handleCancel} disabled={actionLoading} data-testid="confirm-cancel-btn">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ja, kündigen"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Plan Dialog */}
      <Dialog open={showChangeDialog} onOpenChange={setShowChangeDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Plan wechseln zu {changeTo === "premium" ? "Premium" : "Basic"}?</DialogTitle>
            <DialogDescription>
              {changeTo === "premium"
                ? "Sie erhalten sofort Zugang zu allen Premium-Funktionen. Die Differenz wird anteilig berechnet."
                : "Sie wechseln zum Basic-Plan. Der Preisunterschied wird gutgeschrieben."}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" className="flex-1" onClick={() => setShowChangeDialog(false)}>Abbrechen</Button>
            <Button className="flex-1 bg-slate-900 hover:bg-slate-800" onClick={handleChangePlan} disabled={actionLoading} data-testid="confirm-change-btn">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Bestätigen"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
